import React, { useCallback, useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';
import { Message, FileAttachment } from '@/types/posit';
// Removed generateMessageId import - no longer creating client-side messages
import WelcomeScreen from './WelcomeScreen';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ChevronDown, Menu } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ChatContainerProps {
  className?: string;
  onSendMessage?: (content: string, attachments?: FileAttachment[]) => Promise<void>;
  externalContent?: string;
  onContentChange?: (content: string) => void;
  messages?: Message[]; // Allow messages to be passed as props
  isLoading?: boolean;  // Allow loading state to be passed as props
}

const ChatContainer: React.FC<ChatContainerProps> = ({ 
  className,
  onSendMessage,
  externalContent,
  onContentChange,
  messages: propMessages,
  isLoading: propIsLoading
}) => {
  const {
    currentPosit,
    messages: contextMessages,
    isLoading: contextIsLoading,
    isStreaming,
    welcomeMode,
    setWelcomeMode,
    toggleSidebar
  } = useChatInterface();

  const location = useLocation();
  
  // Use prop messages if provided, otherwise fall back to context
  const messages = propMessages || contextMessages;
  const isLoading = propIsLoading !== undefined ? propIsLoading : contextIsLoading;
  
  // Removed debug logging - issue resolved
  const isMobile = useIsMobile();
  
  // Detect context based on route
  const isUpdateContext = location.pathname.includes('/update-posit') || location.pathname.includes('/create-posit');

  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Determine if we're on dashboard pages (same logic as Navigation.tsx)
  const isDashboardPage = location.pathname.startsWith('/dashboard') ||
                         location.pathname === '/chat-history' ||
                         location.pathname === '/creator-dashboard' ||
                         location.pathname.startsWith('/chat/') ||
                         location.pathname.startsWith('/update-posit/') ||
                         location.pathname.startsWith('/p/') ||
                         location.pathname.startsWith('/public-posits') ||
                         location.pathname.startsWith('/bug-reports') ||
                         location.pathname.startsWith('/prompt-tester') ||
                         location.pathname.startsWith('/ai-testing-dashboard');

  // Auto-scroll to latest message when new messages are added
  useEffect(() => {
    if (lastMessageRef.current && messages.length > 0 && !welcomeMode) {
      // Scroll the latest message to the top of the container with some padding
      lastMessageRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "start" // This will put the message at the top of the container
      });
    }
  }, [messages, welcomeMode]);

  // Manage typing indicator with delay to prevent immediate flash
  useEffect(() => {
    if (isLoading) {
      // Check if the last message is from user (meaning we're waiting for AI response)
      const lastMessage = messages[messages.length - 1];
      const shouldShowTyping = !lastMessage || lastMessage.role === 'user';
      
      if (shouldShowTyping) {
        // Only show typing indicator after a brief delay (500ms)
        // This prevents the flash when user message is visible but loading is true
        typingTimeoutRef.current = setTimeout(() => {
          setShowTypingIndicator(true);
        }, 500);
      }
    } else {
      // Clear timeout and hide indicator immediately when not loading
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      setShowTypingIndicator(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [isLoading, messages]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    // Check if user is near the bottom (within 100px)
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    // Show scroll button if user scrolled up and there are messages
    if (!isNearBottom && messages.length > 0) {
      setShowScrollButton(true);
      setIsUserScrolling(true);
    } else {
      setShowScrollButton(false);
      setIsUserScrolling(false);
    }
  }, [messages.length]);

  const scrollToBottom = useCallback(() => {
    const scrollContainer = document.querySelector('[data-radix-scroll-area-viewport]');
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
    setShowScrollButton(false);
    setIsUserScrolling(false);
  }, []);

  const handleSendMessage = useCallback(async (content: string, attachments: FileAttachment[] = []) => {
    if ((!content.trim() && attachments.length === 0) || !onSendMessage) return;
    
    try {
      // Exit welcome mode when user sends a message
      if (welcomeMode) {
        setWelcomeMode(false);
      }
      
      // Let the hook handle message creation and persistence
      // This prevents duplicate messages and ensures server-side processing
      await onSendMessage(content, attachments);
      
    } catch (error) {
    }
  }, [onSendMessage, welcomeMode, setWelcomeMode]);

  // Show welcome screen if in welcome mode
  if (welcomeMode) {
    return (
      <div className={cn("flex flex-col h-full", className)}>
        <WelcomeScreen onSubmit={handleSendMessage} />
      </div>
    );
  }

  // Generate placeholder text based on context
  const getPlaceholderText = () => {
    if (!currentPosit) {
      return "Select a Posit to start chatting...";
    }
    
    if (isUpdateContext) {
      // Update context
      if (currentPosit.title === "New Posit Creation") {
        return "What would you like to add to your new Posit?";
      } else {
        return "What would you like to change or add?";
      }
    } else {
      // Chat context
      return "Ask anything";
    }
  };

  return (
    <div className={cn("flex flex-col h-full relative", className)}>
      {/* Floating Sidebar Toggle - Only show if not on dashboard pages */}
      {!isDashboardPage && isMobile && (
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className="absolute top-4 left-4 z-10"
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {/* Messages Area */}
      <div className="flex-1 relative min-h-0">
        <ScrollArea 
          className="h-full"
          onScrollCapture={handleScroll}
        >
          <div className="space-y-0 pt-4 pb-8 px-4 md:pt-6 max-w-4xl mx-auto">
            {messages.map((message, index) => (
              <div
                key={message.id}
                ref={index === messages.length - 1 ? lastMessageRef : null}
              >
                <MessageBubble
                  message={message}
                  variant={message.role === 'user' ? 'user' : 'assistant'}
                  showTimestamp={
                    index === 0 || 
                    index === messages.length - 1 ||
                    (index > 0 && 
                      new Date(message.created_at).getTime() - 
                      new Date(messages[index - 1].created_at).getTime() > 300000
                    )
                  }
                  isStreaming={isStreaming && index === messages.length - 1}
                />
              </div>
            ))}
            
            {/* Loading indicator for new messages - with delay to prevent flash */}
            {showTypingIndicator && (
              <div className="px-8 py-6">
                <div className="max-w-4xl mx-auto">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <Button
            onClick={scrollToBottom}
            size="sm"
            className="absolute bottom-4 right-4 rounded-full shadow-lg hover:shadow-xl transition-shadow z-10"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Chat Input */}
      <ChatInput
        variant="conversation"
        onSubmit={handleSendMessage}
        disabled={isLoading || !currentPosit}
        placeholder={getPlaceholderText()}
        externalValue={externalContent}
        onExternalChange={onContentChange}
      />
    </div>
  );
};

export default ChatContainer; 